import React, { useContext, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Alert, Button, Layout, Space } from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";
import AdminNavigationMenu from "@/Components/Common/AdminNavigationMenu.jsx";
import { AdminPublicHeader } from "@/Components/Common/AdminPublicHeader";
import AppLayout from "@/Components/Common/AppLayout.jsx";
import { CommonHeader } from "@/Components/Common/CommonHeader.jsx";
import { HomePageHeader } from "@/Components/Common/HomePageHeader.jsx";
import { useAuth } from "@/Context/AuthContext";
import { ToastContext } from "@/Context/ToastContext";
import { UserContext } from "@/Context/UserContext";
import { getLocalStorageItem, isAdmin, isAdminPanelAccess } from "@/Utils/Index";
import { accessCondition } from "../Assets/Mocks/Access.mock.js";
import { PageAccessPermission } from "../Components/Common/AccessPermission.jsx";
import { PublicFooter } from "../Components/Common/PublicFooter.jsx";
import NavigationMenu from "./Navigation.jsx";

export const AppRoute = ({ type }) => {
  const { isAuthenticated, login } = useAuth();
  const [loading, setLoading] = useState(true);
  const { userProfileData, updateProfileInfo } = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [requiredAccess, setRequiredAccess] = useState([]);
  const [useEffectCompleted, setUseEffectCompleted] = useState(false);
  const [isNavigationVisible, setIsNavigationVisible] = useState(false);
  const { showToast } = useContext(ToastContext);
  const [countdown, setCountdown] = useState(2);

  const fetch = useAuthenticatedFetch();
  const fetchUser = async () => {
    setLoading(true);
    try {
      const user = await fetch.get(`/user`);
      updateProfileInfo(user?.data);
      login();
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!isAuthenticated && getLocalStorageItem("authorization")) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [userProfileData]);

  useEffect(() => {
    const halfHour = 30 * 60 * 1000;
    const updatedTime = new Date(userProfileData?.updated).getTime();
    const now = Date.now();
    const timeElapsed = now - updatedTime;
    const timeLeft = halfHour - timeElapsed;
    const timeLeftInSeconds = Math.ceil(timeLeft / 1000);
    setCountdown(timeLeftInSeconds);
  }, [userProfileData]);

  useEffect(() => {
    let interval;
    if (countdown > 0) {
      interval = setInterval(() => {
        if (countdown > 0) {
          setCountdown(countdown - 1);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [countdown]);

  useEffect(() => {
    const jobPathRegex = /^\/jobs\/([a-zA-Z0-9]+)$/;
    const match = location.pathname.match(jobPathRegex);
    const path = match ? "/jobs/id" : location.pathname;
    const foundObject = accessCondition.find((obj) => obj.url === path);
    setRequiredAccess(foundObject?.requiredRoles || []);
    setUseEffectCompleted(true);
  }, [location, accessCondition, userProfileData]);

  const handleButtonClick = async () => {
    const res = await fetch.post(`/resend`, { email: userProfileData?.email });
    showToast("The email has been resent successfully. Kindly check your inbox.");
  };

  if (loading) {
    return <></>;
  }
  if (type === "public") {
    // Allow both authenticated and non-authenticated users to see public routes
    // Only redirect to /home if they're on login/signup pages and already authenticated
    const isAuthPage = location.pathname === "/login" || location.pathname === "/signup";

    if (isAuthenticated && isAuthPage) {
      return <Navigate to="/home" />;
    }

    return (
      <CommonHeader>
        <div className="page-container-box">
          <Outlet />
        </div>
      </CommonHeader>
    );
  }
  if (type === "private") {
    return isAuthenticated ? (
      <Layout style={{ minHeight: "100vh" }}>
        <CommonHeader />
        <Layout.Content style={{ padding: "0", flex: 1 }}>
          <div className="page-container-box">
            <NavigationMenu
              setIsNavigationVisible={setIsNavigationVisible}
              setUseEffectCompleted={setUseEffectCompleted}
            />
            {useEffectCompleted && (
              <PageAccessPermission access={requiredAccess}>
                <Layout.Content style={{ padding: "0", width: "100%" }}>
                  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    {!userProfileData.verified && (
                      <>
                        {countdown < 0 && (
                          <Alert
                            type="warning"
                            message={
                              <Space size="large">
                                Verify your email to secure your account and receive regular software updates,
                                including new features and tips.
                                <Button type="plain" onClick={handleButtonClick}>
                                  Resend Email
                                </Button>
                              </Space>
                            }
                            showIcon
                          />
                        )}
                      </>
                    )}
                    <Outlet />
                  </Space>
                </Layout.Content>
              </PageAccessPermission>
            )}
          </div>
        </Layout.Content>
      </Layout>
    ) : (
      <Navigate to="/login" />
    );
  }
};

export const GeneralRoute = () => {
  const location = useLocation();
  const [showHeaderFooter, setShowHeaderFooter] = useState(false);

  useEffect(() => {
    const isAssessmentPage =
      location.pathname.includes("/assessment/") || location.pathname.includes("/stage/share/");
    const isResumeRoute =
      location.pathname.includes("/app/resume/") || location.pathname.startsWith("/app/resume");
    const isThankYouPage = location.pathname === "/app/thank-you";
    // Check for auth pages (login/signup) more explicitly
    const isLoginPage = location.pathname === "/app/login" || location.pathname.startsWith("/app/login");
    const isSignupPage = location.pathname === "/app/signup" || location.pathname.startsWith("/app/signup");
    const isAuthPage = isLoginPage || isSignupPage;

    // Show header/footer only if it's not an assessment page AND not a resume route AND not thank you page AND not auth page
    const shouldShow = !isAssessmentPage && !isResumeRoute && !isThankYouPage && !isAuthPage;
    setShowHeaderFooter(shouldShow);
  }, [location]);

  return (
    <>
      {/* {showHeaderFooter && <HomePageHeader />} */}
      <Outlet />
      {/* {showHeaderFooter && <CommonFooter />} */}
    </>
  );
};

export const PublicAdminRoute = () => {
  return (
    <>
      <AdminPublicHeader />
      <Outlet />
    </>
  );
};

export const AppLayoutRoute = () => {
  const { isAuthenticated, login } = useAuth();
  const [loading, setLoading] = useState(true);
  const { userProfileData, updateProfileInfo } = useContext(UserContext);
  const location = useLocation();
  const fetch = useAuthenticatedFetch();

  // Handle adminToken in URL - convert to regular user session
  const urlParams = new URLSearchParams(location.search);
  const adminTokenFromUrl = urlParams.get("adminToken");

  useEffect(() => {
    if (adminTokenFromUrl) {
      // Store as regular user auth in localStorage
      window.localStorage.setItem("authorization", adminTokenFromUrl);

      // Decode JWT to extract userId
      try {
        const base64Url = adminTokenFromUrl.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const payload = JSON.parse(atob(base64));
        if (payload._id) {
          window.localStorage.setItem("userId", payload._id);
        }
      } catch (e) {
        // Token decode failed
      }

      // Redirect to clean URL (without adminToken) to load as regular user
      window.location.href = location.pathname;
    }
  }, [adminTokenFromUrl]);

  // Show loading while processing adminToken redirect
  if (adminTokenFromUrl) {
    return <></>;
  }

  const fetchUser = async () => {
    setLoading(true);
    try {
      const user = await fetch.get(`/user`);
      updateProfileInfo(user?.data);
      login();
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated && getLocalStorageItem("authorization")) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [userProfileData]);

  if (loading) {
    return <></>;
  }

  return isAuthenticated ? (
    <AppLayout>
      <Outlet />
    </AppLayout>
  ) : (
    <Navigate to="/app/login" />
  );
};

// PROTECTED RESUME ROUTE - Forces login before resume access
// Note: Ownership validation is handled by backend APIs when needed
export const ProtectedResumeRoute = () => {
  const { isAuthenticated, login } = useAuth();
  const [loading, setLoading] = useState(true);
  const { userProfileData, updateProfileInfo } = useContext(UserContext);
  const location = useLocation();
  const fetch = useAuthenticatedFetch();

  // Check if admin is accessing via admin token (from URL or sessionStorage)
  const urlParams = new URLSearchParams(location.search);
  const adminTokenFromUrl = urlParams.get("adminToken");
  const adminTokenFromSession = sessionStorage.getItem("adminResumeAccessToken");
  const isAdminAccess = !!(adminTokenFromUrl || adminTokenFromSession);

  // Store admin token in sessionStorage if present in URL
  useEffect(() => {
    if (adminTokenFromUrl) {
      sessionStorage.setItem("adminResumeAccessToken", adminTokenFromUrl);
    }
  }, [adminTokenFromUrl]);

  const fetchUser = async () => {
    setLoading(true);
    try {
      const user = await fetch.get(`/user`);
      updateProfileInfo(user?.data);
      login();
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Skip user fetch for admin access - admin doesn't need user profile
    if (isAdminAccess) {
      setLoading(false);
      return;
    }

    if (!isAuthenticated && getLocalStorageItem("authorization")) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [userProfileData, isAdminAccess]);

  if (loading) {
    return <></>;
  }

  // ADMIN ACCESS - Allow through without regular auth check
  if (isAdminAccess) {
    return <Outlet />;
  }

  // NOT LOGGED IN = REDIRECT TO LOGIN
  if (!isAuthenticated) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/app/login?returnUrl=${returnUrl}`} replace />;
  }

  return <Outlet />;
};

export const PrivateAdminRoute = () => {
  if (!isAdminPanelAccess()) {
    return <Navigate to="/app/admin/login" replace />;
  }

  return (
    <>
      <style>{`
        /* Fix admin panel top spacing by overriding global body padding */
        body {
          padding-top: 0 !important;
        }
      `}</style>
      <Layout style={{ minHeight: "100vh" }}>
        <Layout.Header style={{ padding: 0, background: "#fff" }}>
          <AdminPublicHeader />
        </Layout.Header>
        <Layout>
          <Layout.Sider width={240} style={{ background: "#fff" }}>
            <AdminNavigationMenu />
          </Layout.Sider>
          <Layout style={{ padding: "24px" }}>
            <Layout.Content
              style={{
                background: "#fff",
                padding: "24px",
                margin: 0,
                minHeight: 280,
              }}
            >
              <Outlet />
            </Layout.Content>
          </Layout>
        </Layout>
      </Layout>
    </>
  );
};
